import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../../apiConfig';
import Timer from './Timer';
import ConfirmationModal from './ConfirmationModal';

const TestModeQuizComponent = React.forwardRef(({ assessment, questions }, ref) => {
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(assessment.duration * 60);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [questionStatus, setQuestionStatus] = useState(
    Array(questions.length).fill('pending')
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [nextPath, setNextPath] = useState(null);

  // Handle browser-level navigation (e.g., closing tab, back/forward)
  useEffect(() => {
    if (quizCompleted || isSubmitting) return;

    const handleBeforeUnload = (event) => {
      if (!showConfirmModal) {
        setShowConfirmModal(true);
        setNextPath('/assessmentDashboard'); // Default fallback
        event.preventDefault();
        // event.returnValue = ''; // Removed to avoid browser dialog
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [quizCompleted, isSubmitting, showConfirmModal]);

  const handleTimeUpdate = useCallback(
    (newTime) => {
      setTimeLeft(newTime);
      if (newTime <= 0 && !quizCompleted && !isSubmitting) {
        handleSubmit();
      }
    },
    [quizCompleted, isSubmitting]
  );

  const handleAnswerChange = useCallback(
    (questionId, optionIndex) => {
      setAnswers((prev) => {
        const currentAnswers = prev[questionId] || [];
        const question = questions.find((q) => q._id === questionId);
        if (!question) return prev;

        let newAnswers;
        if (question.type === 'single') {
          newAnswers = [optionIndex];
        } else {
          if (currentAnswers.includes(optionIndex)) {
            newAnswers = currentAnswers.filter((idx) => idx !== optionIndex);
          } else {
            newAnswers = [...currentAnswers, optionIndex];
          }
        }

        return { ...prev, [questionId]: newAnswers };
      });

      setQuestionStatus((prev) => {
        const newStatus = [...prev];
        newStatus[currentQuestionIndex] = 'answered';
        return newStatus;
      });
    },
    [currentQuestionIndex, questions]
  );

  const handleSkip = useCallback(() => {
    setQuestionStatus((prev) => {
      const newStatus = [...prev];
      newStatus[currentQuestionIndex] = 'skipped';
      return newStatus;
    });
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      handleSubmit();
    }
  }, [currentQuestionIndex, questions.length]);

  const handleNext = useCallback(() => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  }, [currentQuestionIndex, questions.length]);

  const handlePrevious = useCallback(() => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  }, [currentQuestionIndex]);

 const handleSubmit = useCallback(async () => {
  if (isSubmitting || quizCompleted) return;
  setIsSubmitting(true);

  try {
    // Calculate timeDuration (total time - time left)
    const timeDuration = assessment.duration * 60 - timeLeft;

    // Create formatted answers, including all questions (answered, skipped, or unanswered)
    const formattedAnswers = questions.map((question) => ({
      questionId: question._id,
      selectedOptions: answers[question._id] || [], // Use existing answers or empty array
    }));

    const response = await axios.post(
      `${API_URL}/api/v1/admin/public/assessments/${assessment.slug}/attempts`,
      { answers: formattedAnswers, timeDuration }, // Add timeDuration to payload
      { withCredentials: true }
    );

    if (response.data.success) {
      toast.success('Assessment submitted successfully', { autoClose: 2500 });
      setQuizCompleted(true);
    } else {
      toast.error(response.data.message || 'Failed to submit assessment', { autoClose: 2500 });
    }
  } catch (error) {
    toast.error(error.response?.data?.message || 'Error submitting assessment', {
      autoClose: 2500,
    });
  } finally {
    setIsSubmitting(false);
  }
}, [answers, assessment.slug, assessment.duration, timeLeft, questions, isSubmitting, quizCompleted]);

  const handleQuestionClick = useCallback((index) => {
    setCurrentQuestionIndex(index);
  }, []);

 const calculateScore = useCallback(() => {
  let score = 0;
  questions.forEach((question) => {
    const userAnswers = answers[question._id] || [];
    let isCorrect = false;
    if (question.type === 'multiple') {
      // For multiple-choice questions, award +1 if at least one selected answer is correct
      isCorrect = userAnswers.some((ans) => question.correctAnswers.includes(ans));
      console.log(`Question ID: ${question._id}, Type: multiple, Correct: ${isCorrect}`);
    } else if (question.type === 'single') {
      // For single-choice questions, require exact match
      isCorrect =
        userAnswers.length === question.correctAnswers.length &&
        userAnswers.every((ans) => question.correctAnswers.includes(ans));
      console.log(`Question ID: ${question._id}, Type: single, Correct: ${isCorrect}`);
    }
    if (isCorrect) score += 1;
  });
  return score;
}, [answers, questions]);

  const getSkippedCount = useCallback(() => {
    return questionStatus.filter((status) => status === 'skipped').length;
  }, [questionStatus]);

  const formatTime = useCallback((seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? `0${secs}` : secs}`;
  }, []);

  const getSelectionHint = useCallback((correctAnswersLength) => {
    if (correctAnswersLength === 1) return '(Select One)';
    if (correctAnswersLength === 2) return '(Select Two)';
    return `(Select ${correctAnswersLength})`;
  }, []);

  const handleNavigation = useCallback(
    (path) => {
      if (quizCompleted || isSubmitting) {
        navigate(path);
        return;
      }
      setNextPath(path);
      setShowConfirmModal(true);
    },
    [quizCompleted, isSubmitting, navigate]
  );

  const handleConfirmLeave = useCallback(async () => {
    setQuestionStatus((prev) => {
      const newStatus = [...prev];
      newStatus.forEach((status, index) => {
        if (status === 'pending') {
          newStatus[index] = 'skipped';
        }
      });
      return newStatus;
    });

    await handleSubmit();

    if (nextPath) {
      navigate(nextPath);
    } else {
      navigate('/assessmentDashboard');
    }

    setShowConfirmModal(false);
  }, [handleSubmit, nextPath, navigate]);

  const handleCancelLeave = useCallback(() => {
    setShowConfirmModal(false);
    setNextPath(null);
  }, []);

  React.useImperativeHandle(ref, () => ({
    navigate: handleNavigation,
  }));

  if (quizCompleted) {
    const score = calculateScore();
    const skipped = getSkippedCount();
    return (
      <div className="flex flex-col min-h-screen bg-gray-100">
        <div className="max-w-2xl mx-auto w-full py-12 px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-xl shadow-home-hero p-8">
            <h2 className="text-3xl font-raleway font-extrabold text-primary mb-4">
              Assessment Completed!
            </h2>
            {assessment.resultMode === 1 && (
              <p className="text-lg font-montserrat text-secondary mb-2">
                Your score: {score} out of {questions.length}
              </p>
            )}
            <p className="text-lg font-montserrat text-secondary mb-8">
             Thanks For completing assessments
            </p>
            <div className="flex justify-center space-x-4">
              {assessment.resultMode === 1 && (
                <button
                  onClick={() => navigate(`/assessments/${assessment.slug}/results`)}
                  className="admin-btn btn-primary px-8 py-3 rounded-full font-montserrat text-sm"
                >
                  View Results
                </button>
              )}
              <button
                onClick={() => navigate('/assessmentDashboard')}
                className="admin-btn btn-outlined px-8 py-3 rounded-full font-montserrat text-sm"
              >
                Back to Assessments
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  return (
    <div className="flex flex-col min-h-screen bg-gray-100">
      <ConfirmationModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
        message="Are you sure you want to leave?"
      />
      <Timer
        initialTime={assessment.duration * 60}
        onTimeUpdate={handleTimeUpdate}
        isRunning={!quizCompleted && !isSubmitting}
      />
      <div className="flex flex-1">
        <div className="w-64 bg-white shadow-home-hero p-4">
          <h3 className="text-lg font-raleway font-bold text-primary mb-4">Progress</h3>
          <ul className="space-y-2">
            {questions.map((_, index) => (
              <li
                key={index}
                onClick={() => handleQuestionClick(index)}
                className={`flex items-center space-x-2 p-2 rounded-lg font-montserrat text-sm cursor-pointer ${
                  currentQuestionIndex === index
                    ? 'bg-primary text-white'
                    : questionStatus[index] === 'answered'
                    ? 'bg-gray-200 text-gray-600'
                    : 'bg-white text-secondary border border-gray-200'
                }`}
              >
                <span>Question {index + 1}</span>
                {questionStatus[index] === 'skipped' && (
                  <i className="fas fa-forward text-secondary"></i>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-4">
            <p className="text-sm font-montserrat text-secondary">
              Attempted: {questionStatus.filter((s) => s === 'answered').length}
            </p>
            <p className="text-sm font-montserrat text-secondary">
              Skipped: {questionStatus.filter((s) => s === 'skipped').length}
            </p>
            <p className="text-sm font-montserrat text-secondary">
              Remaining: {questionStatus.filter((s) => s === 'pending').length}
            </p>
          </div>
        </div>
        <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-raleway font-bold text-dark">{assessment.title}</h2>
            <div className="bg-primary text-white px-3 py-1 rounded-full text-sm font-montserrat">
              Time Left: {formatTime(timeLeft)}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-home-hero p-6 transition-all duration-300">
            <h3 className="text-xl font-raleway font-semibold text-primary mb-4">
              Question {currentQuestionIndex + 1} of {questions.length}
            </h3>
            <p className="text-secondary text-lg font-montserrat mb-6">
              {currentQuestion.questionText}{' '}
              <span className="text-sm text-gray-500">
                {getSelectionHint(currentQuestion.correctAnswers.length)}
              </span>
            </p>
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => (
                <label
                  key={index}
                  className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition duration-150 cursor-pointer"
                >
                  <input
                    type={currentQuestion.type === 'single' ? 'radio' : 'checkbox'}
                    name={currentQuestion._id}
                    checked={(answers[currentQuestion._id] || []).includes(index)}
                    onChange={() => handleAnswerChange(currentQuestion._id, index)}
                    className="h-5 w-5 text-primary focus:ring-primary"
                    disabled={quizCompleted || isSubmitting}
                  />
                  <span className="text-secondary text-base font-montserrat">{option}</span>
                </label>
              ))}
            </div>
            <div className="mt-8 flex justify-end space-x-3">
              <button
                onClick={handlePrevious}
                disabled={isFirstQuestion || quizCompleted || isSubmitting}
                className={`admin-btn btn-outlined px-6 py-2 rounded-full font-montserrat text-sm ${
                  isFirstQuestion || quizCompleted || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Previous
              </button>
              <button
                onClick={handleSkip}
                disabled={quizCompleted || isSubmitting}
                className={`admin-btn btn-outlined px-6 py-2 rounded-full font-montserrat text-sm ${
                  quizCompleted || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Skip
              </button>
              {isLastQuestion ? (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting || quizCompleted}
                  className={`admin-btn btn-primary px-6 py-2 rounded-full font-montserrat text-sm ${
                    isSubmitting || quizCompleted ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  Submit
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  disabled={quizCompleted || isSubmitting}
                  className={`admin-btn btn-primary px-6 py-2 rounded-full font-montserrat text-sm ${
                    quizCompleted || isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  Next
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default TestModeQuizComponent;